// src/pages/CashflowPage.js
// จัดการเงินสด & เงินโอน ในร้าน: ใช้ endpoints /cashflow/*

import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import api from '../api';
import AuthContext from '../AuthContext';
import { useNotification } from '../NotificationContext';
import OrderDetailModal from '../components/OrderDetailModal';
import ExpenseDetailModal from '../components/ExpenseDetailModal';
import DepositDetailModal from '../components/DepositDetailModal';
import SkeletonLoader from '../components/SkeletonLoader';

const CashflowPage = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const { addNotification } = useNotification();

  // Balances (Admin only)
  const [cashBalance, setCashBalance] = useState(0);
  const [transferBalance, setTransferBalance] = useState(0);
  // Initial balances (from backend summary) for display
  const [initialCash, setInitialCash] = useState(0);
  const [initialTransfer, setInitialTransfer] = useState(0);
  const [initialCashAt, setInitialCashAt] = useState(null);
  const [initialTransferAt, setInitialTransferAt] = useState(null);

  // Operation state
  const [loading, setLoading] = useState(false);

  // Helper: Get local date string YYYY-MM-DD (not UTC)
  const getLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = new Date();
  const todayIso = getLocalDateString(todayDate);
  const firstDayIso = getLocalDateString(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));

  // 7 days ago limit for staff
  const sevenDaysAgoIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return getLocalDateString(d);
  }, []);

  // Daily Summary State (For both Admin and Staff)
  const [dailyDate, setDailyDate] = useState(todayIso);
  const [dailySummary, setDailySummary] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  // Quick date shortcuts
  const quickDates = useMemo(() => {
    const list = [];
    const limit = isAdmin ? 5 : 7;
    for (let i = 1; i <= limit; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = getLocalDateString(d);
      const label = i === 1 ? 'เมื่อวาน' : `${i} วันก่อน`;
      list.push({ label, date: iso });
    }
    return list;
  }, [isAdmin]);

  // Movements list
  const [movements, setMovements] = useState([]);
  const [filters, setFilters] = useState({
    date_from: isAdmin ? firstDayIso : sevenDaysAgoIso,
    date_to: todayIso,
    movement_type: ''
  });
  const [selectedMonth, setSelectedMonth] = useState(''); // format YYYY-MM
  const [movementsLoading, setMovementsLoading] = useState(false);

  // Order detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);

  // Expense detail modal
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [loadingExpense, setLoadingExpense] = useState(false);

  // Deposit detail modal
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  // Helpers
  const fmt = v => (Number(v) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Load balances (Admin only)
  const loadSummary = async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/cashflow/summary');
      setCashBalance(res.data.cash_balance || 0);
      setTransferBalance(res.data.transfer_balance || 0);
      setInitialCash(res.data.initial_cash || 0);
      setInitialTransfer(res.data.initial_transfer || 0);
      setInitialCashAt(res.data.initial_cash_at || null);
      setInitialTransferAt(res.data.initial_transfer_at || null);
    } catch (err) {
      console.error('loadSummary error', err);
    }
  };

  // Load daily summary
  const loadDailySummary = useCallback(async (targetDate) => {
    const dateToFetch = targetDate || dailyDate;
    setDailyLoading(true);
    try {
      const res = await api.get('/cashflow/daily-summary', { params: { date: dateToFetch } });
      setDailySummary(res.data);
    } catch (err) {
      addNotification(err.response?.data?.message || 'โหลดสรุปรายวันไม่สำเร็จ', 'danger');
    } finally {
      setDailyLoading(false);
    }
  }, [dailyDate, addNotification]);

  // Load movements
  const loadMovements = useCallback(async () => {
    setMovementsLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.movement_type) params.movement_type = filters.movement_type;
      const res = await api.get('/cashflow/movements', { params });
      setMovements(res.data || []);
    } catch (err) {
      addNotification('โหลดรายการเคลื่อนไหวล้มเหลว', 'danger');
      console.error(err);
    } finally {
      setMovementsLoading(false);
    }
  }, [filters, addNotification]);

  // Initial load
  useEffect(() => {
    if (isAdmin) {
      loadSummary();
    }
    loadDailySummary(dailyDate);
    loadMovements();
  }, [isAdmin]);

  // When dailyDate changes
  useEffect(() => {
    loadDailySummary(dailyDate);
  }, [dailyDate]);

  // No grouping; just show movements as-is (sorted by created_at desc)
  const groupedMovements = useMemo(() => {
    const result = Array.isArray(movements) ? [...movements] : [];
    result.sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));
    return result;
  }, [movements]);

  // Modal for entering amount+note and confirm
  const [confirmModal, setConfirmModal] = useState({ show: false, action: '', amount: '', note: '', title: '', summary: '' });

  // Handlers for cash movements
  const handleInitiateMove = (action) => {
    // Open modal first; user will fill amount+note then confirm
    const cfg = action === 'move_cash_to_bank'
      ? { title: 'นำเงินสดเข้าบัญชี', summary: 'กรอกจำนวนเงินและหมายเหตุ (ถ้ามี) แล้วกดยืนยัน' }
      : { title: 'เบิกเงินสดออกจากบัญชี', summary: 'กรอกจำนวนเงินและหมายเหตุ (ถ้ามี) แล้วกดยืนยัน' };
    setConfirmModal({ show: true, action, amount: '', note: '', title: cfg.title, summary: cfg.summary });
  };

  const handleSetInitialBalance = (balanceType) => {
    const cfg = balanceType === 'cash'
      ? { title: 'ตั้งค่ายอดเงินสดเริ่มต้น', summary: 'กรอกจำนวนเงินสดเริ่มต้นในร้าน (จะถูกบันทึกในรายการเคลื่อนไหว)' }
      : { title: 'ตั้งค่ายอดบัญชีเริ่มต้น', summary: 'กรอกยอดเงินเริ่มต้นในบัญชีธนาคาร (จะถูกบันทึกในรายการเคลื่อนไหว)' };
    setConfirmModal({ show: true, action: `set_initial_${balanceType}`, amount: '', note: 'ตั้งค่ายอดเริ่มต้น', title: cfg.title, summary: cfg.summary });
  };

  const handleConfirmMove = async () => {
    setLoading(true);
    try {
      const amt = Number(confirmModal.amount);
      if (isNaN(amt)) { addNotification('จำนวนเงินไม่ถูกต้อง', 'warning'); return; }
      if (amt > 10000000) { addNotification('จำนวนเงินสูงเกินไป', 'warning'); return; }

      // Handle initial balance setting
      if (confirmModal.action.startsWith('set_initial_')) {
        if (amt < 0) { addNotification('จำนวนเงินต้องไม่ติดลบ', 'warning'); return; }
        // อนุญาตให้ตั้งยอดเริ่มต้น = 0 ได้ตามคำขอ

        const balanceType = confirmModal.action === 'set_initial_cash' ? 'cash' : 'transfer';
        await api.post('/cashflow/set-initial-balance', {
          balance_type: balanceType,
          amount: amt,
          note: (confirmModal.note || 'ตั้งค่ายอดเริ่มต้น').trim()
        }).then(r => {
          addNotification('ตั้งค่ายอดเริ่มต้นสำเร็จ', 'success');
        }).catch(err => {
          if (err.response?.status === 409) {
            addNotification(err.response.data?.message || 'ยอดตั้งต้นถูกล็อกแล้ว', 'warning');
          } else {
            throw err;
          }
        });
        setConfirmModal({ show: false, action: '', amount: '', note: '', title: '', summary: '' });
        await loadSummary();
        await loadMovements();
        setLoading(false);
        return;
      }

      // Handle regular movements
      if (!amt || amt <= 0) { addNotification('กรุณากรอกจำนวนเงินมากกว่า 0', 'warning'); return; }
      if (confirmModal.action === 'move_cash_to_bank' && amt > cashBalance) {
        addNotification('จำนวนเงินสดไม่พอ', 'danger'); return;
      }
      if (confirmModal.action === 'withdraw_cash_from_bank' && amt > transferBalance) {
        addNotification('ยอดในบัญชีไม่พอ', 'danger'); return;
      }

      await api.post('/cashflow/move', {
        action: confirmModal.action,
        amount: amt,
        note: (confirmModal.note || '').trim()
      });

      const successMsg = confirmModal.action === 'move_cash_to_bank'
        ? 'บันทึกการนำเงินสดเข้าบัญชีสำเร็จ'
        : 'บันทึกการเบิกเงินสดสำเร็จ';

      addNotification(successMsg, 'success');
      setConfirmModal({ show: false, action: '', amount: '', note: '', title: '', summary: '' });
      await loadSummary();
      await loadMovements();
    } catch (err) {
      addNotification(err.response?.data?.message || 'บันทึกไม่สำเร็จ', 'danger');
    } finally { setLoading(false); }
  };

  // Filter handlers
  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  const handleMonthChange = e => {
    const value = e.target.value; // YYYY-MM
    setSelectedMonth(value);
    if (!value) return;
    const [year, month] = value.split('-').map(v => parseInt(v, 10));
    if (year && month) {
      const first = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const last = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      setFilters(prev => ({ ...prev, date_from: first, date_to: last }));
      // Auto apply after month picked
      setTimeout(() => { loadMovements(); }, 10);
    }
  };
  const applyFilters = () => loadMovements();
  const clearFilters = () => {
    const today = new Date();
    const t = getLocalDateString(today);
    const fromDate = isAdmin ? firstDayIso : sevenDaysAgoIso;
    setFilters({ date_from: fromDate, date_to: t, movement_type: '' });
    setSelectedMonth('');
    setTimeout(loadMovements, 50);
  };

  // Order detail
  const handleShowOrderDetail = async orderId => {
    if (!orderId) return;
    setLoadingOrderDetail(true);
    setSelectedOrderDetail(null);
    setIsDetailModalOpen(true);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setSelectedOrderDetail(res.data);
    } catch (err) {
      addNotification(err.response?.data?.message || 'โหลดรายละเอียดออเดอร์ไม่สำเร็จ', 'danger');
    } finally { setLoadingOrderDetail(false); }
  };

  // Expense detail
  const handleShowExpenseDetail = async (expenseId) => {
    if (!expenseId) return;
    setLoadingExpense(true);
    setSelectedExpense(null);
    setIsExpenseModalOpen(true);
    try {
      const res = await api.get(`/expenses/${expenseId}`);
      setSelectedExpense(res.data);
    } catch (err) {
      addNotification(err.response?.data?.message || 'โหลดรายละเอียดรายจ่ายไม่สำเร็จ', 'danger');
    } finally { setLoadingExpense(false); }
  };

  // Deposit detail
  const handleShowDepositDetail = async (depositId) => {
    if (!depositId) return;
    setSelectedDeposit(null);
    setIsDepositModalOpen(true);
    try {
      const res = await api.get(`/deposits/${depositId}`);
      setSelectedDeposit(res.data);
    } catch (err) {
      addNotification(err.response?.data?.message || 'โหลดรายละเอียดมัดจำไม่สำเร็จ', 'danger');
    }
  };

  // Removed bill detail handler

  // Total amount for footer - properly calculate net flow
  // Inflows (+): cash_in, transfer_in, deposit_cash_in, deposit_transfer_in, initial_balance_cash, initial_balance_transfer, withdraw_cash_from_bank
  // Outflows (-): expense_cash_out, expense_transfer_out, deposit_refund_cash, deposit_refund_transfer, move_cash_to_bank
  // Note: move_cash_to_bank and withdraw_cash_from_bank are internal transfers; for "total" view they cancel out
  const totals = useMemo(() => {
    let totalIn = 0;
    let totalOut = 0;
    groupedMovements.forEach(m => {
      const amt = Number(m.amount) || 0;
      const type = m.movement_type || '';

      // Income types
      if (['cash_in', 'transfer_in', 'deposit_cash_in', 'deposit_transfer_in', 'initial_balance_cash', 'initial_balance_transfer'].includes(type)) {
        totalIn += amt;
      }
      // Expense/Outflow types
      else if (['expense_cash_out', 'expense_transfer_out', 'deposit_refund_cash', 'deposit_refund_transfer'].includes(type)) {
        totalOut += amt;
      }
      // Internal transfers (move between cash/bank) - don't count in net, but show separately if needed
      // move_cash_to_bank: cash goes out, bank goes in -> net 0
      // withdraw_cash_from_bank: bank goes out, cash goes in -> net 0
    });
    return { totalIn, totalOut, net: totalIn - totalOut };
  }, [groupedMovements]);

  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filters, pageSize]);
  const totalCount = groupedMovements.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageEnd = Math.min(totalCount, pageStart + pageSize);
  const pagedMovements = groupedMovements.slice(pageStart, pageEnd);

  return (
    <div className="dashboard-page cashflow-page container py-3">
      {/* Hero */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-cash-coin me-2"></i>จัดการเงินสด &amp; เงินโอน
          </div>
          <div className="dashboard-hero-subtitle">สรุปยอดและรายการเคลื่อนไหวเงินในร้าน</div>
        </div>
      </div>

      {/* Balance cards (Admin Only) */}
      {isAdmin && (
        <>
          <div className="row g-3 mb-4">
            {/* Cash Balance Card */}
            <div className="col-md-6">
              <div className="card card-ks-soft h-100 border-start border-5 border-success">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-success bg-opacity-10 p-3 rounded-circle me-3">
                      <i className="bi bi-cash fs-3 text-success"></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="text-muted mb-1">เงินสดในร้าน</h6>
                        {initialCash !== 0 && (
                          <span className="badge bg-success-subtle text-success border border-success">
                            <i className="bi bi-check-circle me-1"></i>ตั้งค่าแล้ว
                          </span>
                        )}
                      </div>
                      <h3 className="mb-0 fw-bold text-success">{fmt(cashBalance)} ฿</h3>
                    </div>
                  </div>
                  <div className="small text-muted mt-2 mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    {initialCashAt ? (
                      <>
                        <div>ยอดตั้งต้น: <strong>{fmt(initialCash)} ฿</strong></div>
                        <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-calendar-check me-1"></i>
                          ตั้งค่าเมื่อ: {new Date(initialCashAt).toLocaleString('th-TH')}
                        </div>
                      </>
                    ) : (
                      <span className="text-warning"><i className="bi bi-exclamation-triangle me-1"></i>ยังไม่ได้ตั้งค่ายอดเริ่มต้น</span>
                    )}
                  </div>
                  {!initialCashAt && (
                    <>
                      <button
                        type="button"
                        className="btn btn-success btn-sm w-100"
                        onClick={() => handleSetInitialBalance('cash')}
                        disabled={loading}
                      >
                        <i className="bi bi-gear-fill me-1"></i>ตั้งค่ายอดเงินสดเริ่มต้น
                      </button>
                      <div className="alert alert-warning mt-2 mb-0 py-2 small">
                        <i className="bi bi-info-circle me-1"></i>
                        <strong>สำคัญ:</strong> ตั้งค่าได้เพียงครั้งเดียว และจะถูกล็อกหลังบันทึก
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Transfer Balance Card */}
            <div className="col-md-6">
              <div className="card card-ks-soft h-100 border-start border-5" style={{ borderColor: "#d4af37" }}>
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="p-3 rounded-circle me-3" style={{ backgroundColor: "rgba(212, 175, 55, 0.1)" }}>
                      <i className="bi bi-bank fs-3" style={{ color: "#d4af37" }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="text-muted mb-1">ยอดในบัญชี (โอน)</h6>
                        {initialTransfer !== 0 && (
                          <span className="badge border" style={{ backgroundColor: "rgba(212, 175, 55, 0.1)", color: "#d4af37", borderColor: "#d4af37" }}>
                            <i className="bi bi-check-circle me-1"></i>ตั้งค่าแล้ว
                          </span>
                        )}
                      </div>
                      <h3 className="mb-0 fw-bold" style={{ color: "#d4af37" }}>{fmt(transferBalance)} ฿</h3>
                    </div>
                  </div>
                  <div className="small text-muted mt-2 mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    {initialTransferAt ? (
                      <>
                        <div>ยอดตั้งต้น: <strong>{fmt(initialTransfer)} ฿</strong></div>
                        <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-calendar-check me-1"></i>
                          ตั้งค่าเมื่อ: {new Date(initialTransferAt).toLocaleString('th-TH')}
                        </div>
                      </>
                    ) : (
                      <span className="text-warning"><i className="bi bi-exclamation-triangle me-1"></i>ยังไม่ได้ตั้งค่ายอดเริ่มต้น</span>
                    )}
                  </div>
                  {!initialTransferAt && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm w-100"
                        style={{ backgroundColor: "#d4af37", borderColor: "#d4af37", color: "#ffffff" }}
                        onClick={() => handleSetInitialBalance('transfer')}
                        disabled={loading}
                      >
                        <i className="bi bi-gear-fill me-1"></i>ตั้งค่ายอดบัญชีเริ่มต้น
                      </button>
                      <div className="alert alert-warning mt-2 mb-0 py-2 small">
                        <i className="bi bi-info-circle me-1"></i>
                        <strong>สำคัญ:</strong> ตั้งค่าได้เพียงครั้งเดียว และจะถูกล็อกหลังบันทึก
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cash movement actions */}
          <div className="card card-ks-soft mb-4">
            <div className="card-body">
              <h5 className="mb-3 fw-bold text-secondary"><i className="bi bi-arrow-left-right me-2"></i>เคลื่อนไหวเงิน</h5>
              <div className="d-flex flex-column flex-md-row gap-2">
                <button type="button" className="btn btn-primary flex-grow-1" disabled={loading} onClick={() => handleInitiateMove('move_cash_to_bank')}>
                  <i className=" bi bi-box-arrow-in-right me-2"></i>นำเงินสดเข้าบัญชี
                </button>
                <button type="button" className="btn btn-warning text-white flex-grow-1" disabled={loading} onClick={() => handleInitiateMove('withdraw_cash_from_bank')}>
                  <i className="bi bi-box-arrow-right me-2"></i>เบิกเงินสดออกจากบัญชี
                </button>
              </div>
              <div className="small text-muted mt-2">
                <i className="bi bi-shield-lock me-1"></i>* สำหรับผู้มีสิทธิ์ Admin เท่านั้น
              </div>
            </div>
          </div>
        </>
      )}

      {/* New Card: Daily Financial Stream Summary (แยก เงินสด / เงินโอน) */}
      <div className="card card-ks-soft mb-4 border-0 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3 border-bottom pb-3">
            <div>
              <h5 className="mb-1 fw-bold text-dark d-flex align-items-center">
                <i className="bi bi-calendar2-week-fill text-primary me-2 fs-4"></i>
                สรุปสายการเงินประจำวัน
              </h5>
              <div className="text-muted small">
                {isAdmin ? (
                  <span><i className="bi bi-shield-check text-success me-1"></i>โหมด Admin (เลือกดูย้อนหลังได้อิสระ)</span>
                ) : (
                  <span><i className="bi bi-clock-history text-info me-1"></i>โหมดพนักงาน (ดูย้อนหลังได้สูงสุด 7 วัน)</span>
                )}
              </div>
            </div>

            {/* Date selection & Quick pills */}
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="d-flex align-items-center gap-2">
                <label className="small text-muted fw-bold text-nowrap mb-0">เลือกวันที่:</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ minWidth: '140px' }}
                  value={dailyDate}
                  min={isAdmin ? undefined : sevenDaysAgoIso}
                  max={todayIso}
                  onChange={(e) => setDailyDate(e.target.value)}
                />
              </div>
              <button
                type="button"
                className={`btn btn-sm ${dailyDate === todayIso ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDailyDate(todayIso)}
              >
                วันนี้
              </button>
              {quickDates.map(qd => (
                <button
                  key={qd.date}
                  type="button"
                  className={`btn btn-sm ${dailyDate === qd.date ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => setDailyDate(qd.date)}
                >
                  {qd.label}
                </button>
              ))}
            </div>
          </div>

          {dailyLoading ? (
            <div className="py-4">
              <SkeletonLoader type="cards" rows={1} columns={3} />
            </div>
          ) : (
            <div className="row g-3">
              {/* สายเงินสด */}
              <div className="col-12 col-md-4">
                <div className="card h-100 border-start border-4 border-success bg-success bg-opacity-10 shadow-none">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-bold text-success fs-6 d-flex align-items-center">
                        <i className="bi bi-cash-stack me-2 fs-5"></i>สายเงินสด (Cash)
                      </span>
                      <span className="badge bg-success-subtle text-success border border-success">
                        {dailySummary?.cash?.orders_count || 0} บิล
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-success border-opacity-25 small">
                      <span className="text-muted">รายรับเงินสด (ขาย+มัดจำ):</span>
                      <strong className="text-success">+{fmt(dailySummary?.cash?.income)} ฿</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-success border-opacity-25 small">
                      <span className="text-muted">รายจ่ายเงินสด (จ่าย+คืนมัดจำ):</span>
                      <strong className="text-danger">-{fmt(dailySummary?.cash?.expense)} ฿</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-2">
                      <span className="fw-bold text-secondary">สุทธิเงินสด:</span>
                      <h5 className={`mb-0 fw-bold ${(dailySummary?.cash?.net || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                        {fmt(dailySummary?.cash?.net)} ฿
                      </h5>
                    </div>
                  </div>
                </div>
              </div>

              {/* สายเงินโอน */}
              <div className="col-12 col-md-4">
                <div className="card h-100 border-start border-4 shadow-none" style={{ borderColor: '#0d6efd', backgroundColor: 'rgba(13, 110, 253, 0.08)' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-bold text-primary fs-6 d-flex align-items-center">
                        <i className="bi bi-credit-card-2-front me-2 fs-5"></i>สายเงินโอน (Transfer)
                      </span>
                      <span className="badge bg-primary-subtle text-primary border border-primary">
                        {dailySummary?.transfer?.orders_count || 0} บิล
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-primary border-opacity-25 small">
                      <span className="text-muted">รายรับเงินโอน (ขาย+มัดจำ):</span>
                      <strong className="text-primary">+{fmt(dailySummary?.transfer?.income)} ฿</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-primary border-opacity-25 small">
                      <span className="text-muted">รายจ่ายเงินโอน (จ่าย+คืนมัดจำ):</span>
                      <strong className="text-danger">-{fmt(dailySummary?.transfer?.expense)} ฿</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-2">
                      <span className="fw-bold text-secondary">สุทธิเงินโอน:</span>
                      <h5 className={`mb-0 fw-bold ${(dailySummary?.transfer?.net || 0) >= 0 ? 'text-primary' : 'text-danger'}`}>
                        {fmt(dailySummary?.transfer?.net)} ฿
                      </h5>
                    </div>
                  </div>
                </div>
              </div>

              {/* รวมทั้งสิ้น */}
              <div className="col-12 col-md-4">
                <div className="card h-100 border-start border-4 border-warning bg-warning bg-opacity-10 shadow-none">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-bold text-dark fs-6 d-flex align-items-center">
                        <i className="bi bi-wallet2 me-2 fs-5 text-warning"></i>รวมทั้งสิ้น (Grand Total)
                      </span>
                      <span className="badge bg-warning-subtle text-dark border border-warning">
                        {dailySummary?.total?.orders_count || 0} บิล / {dailySummary?.total?.expenses_count || 0} จ่าย
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-warning border-opacity-25 small">
                      <span className="text-muted">รายรับรวมทุกช่องทาง:</span>
                      <strong className="text-success">+{fmt(dailySummary?.total?.income)} ฿</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-warning border-opacity-25 small">
                      <span className="text-muted">รายจ่ายรวมทุกช่องทาง:</span>
                      <strong className="text-danger">-{fmt(dailySummary?.total?.expense)} ฿</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-2">
                      <span className="fw-bold text-dark">คงเหลือสุทธิ:</span>
                      <h4 className={`mb-0 fw-bold ${(dailySummary?.total?.net || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                        {fmt(dailySummary?.total?.net)} ฿
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card card-ks-soft">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold text-secondary"><i className="bi bi-clock-history me-2"></i>รายการเคลื่อนไหวล่าสุด</h5>
            {!isAdmin && (
              <span className="badge bg-info-subtle text-info border border-info small">
                <i className="bi bi-info-circle me-1"></i>ดูย้อนหลังได้ไม่เกิน 7 วัน
              </span>
            )}
          </div>
          <div className="row g-2 mb-3">
            {isAdmin && (
              <div className="col-6 col-sm-4 col-md-2">
                <label className="form-label form-label-sm text-muted">เลือกเดือน</label>
                <input type="month" className="form-control form-control-sm" value={selectedMonth} onChange={handleMonthChange} />
              </div>
            )}
            <div className="col-6 col-sm-4 col-md-2">
              <label className="form-label form-label-sm text-muted">ตั้งแต่วันที่</label>
              <input
                type="date"
                name="date_from"
                value={filters.date_from}
                min={isAdmin ? undefined : sevenDaysAgoIso}
                max={todayIso}
                onChange={handleFilterChange}
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-6 col-sm-4 col-md-2">
              <label className="form-label form-label-sm text-muted">ถึงวันที่</label>
              <input
                type="date"
                name="date_to"
                value={filters.date_to}
                min={isAdmin ? undefined : sevenDaysAgoIso}
                max={todayIso}
                onChange={handleFilterChange}
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label form-label-sm text-muted">ประเภทรายการ</label>
              <select name="movement_type" value={filters.movement_type} onChange={handleFilterChange} className="form-select form-select-sm">
                <option value="">-- ทั้งหมด --</option>
                <option value="cash_in">เงินสดจากการขาย</option>
                <option value="transfer_in">ยอดโอนจากการขาย</option>
                <option value="move_cash_to_bank">ฝากเงินสดเข้าบัญชี</option>
                <option value="withdraw_cash_from_bank">เบิกเงินสดจากบัญชี</option>
                <option value="expense_cash_out">จ่ายค่าใช้จ่าย (เงินสด)</option>
                <option value="expense_transfer_out">จ่ายค่าใช้จ่าย (โอน)</option>
              </select>
            </div>
            <div className="col-4 col-sm-2 col-md-1">
              <label className="form-label form-label-sm text-muted">แถว</label>
              <select className="form-select form-select-sm" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="col-8 col-sm-4 col-md-3 d-flex align-items-end gap-2">
              <button className="btn btn-sm btn-secondary flex-grow-1" onClick={applyFilters} disabled={movementsLoading}>
                <i className="bi bi-filter me-1"></i>กรอง
              </button>
              <button className="btn btn-sm btn-outline-secondary flex-grow-1" onClick={clearFilters} disabled={movementsLoading}>
                <i className="bi bi-x-circle me-1"></i>ล้าง
              </button>
            </div>
          </div>

          <div className="table-responsive" style={{ maxHeight: 700 }}>
            <table className="table table-hover align-middle">
              <thead className="table-light text-muted sticky-top">
                <tr>
                  <th style={{ width: 160 }}>วัน/เวลา</th>
                  <th>ประเภท</th>
                  <th>รายการ</th>
                  <th className="text-end" style={{ width: 150 }}>จำนวน</th>
                </tr>
              </thead>
              <tbody>
                {movementsLoading && (
                  <tr>
                    <td colSpan={5} className="py-3">
                      <SkeletonLoader type="table" rows={6} columns={4} />
                    </td>
                  </tr>
                )}
                {!movementsLoading && groupedMovements.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">ไม่พบข้อมูล</td>
                  </tr>
                )}
                {!movementsLoading && pagedMovements.map(m => {
                  const dtStr = m.created_at || m.timestamp;
                  // Backend now sends Local Time string (e.g. 2025-11-24T13:00:00)
                  // Browser will parse this as Local Time correctly without 'Z'
                  const dt = dtStr ? new Date(dtStr) : null;
                  const timeStr = dt ? `${dt.toLocaleDateString('th-TH')} ${dt.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}` : '-';
                  let typeLabel = m.movement_type;
                  let badgeClass = 'bg-secondary';
                  switch (m.movement_type) {
                    case 'cash_in':
                      typeLabel = 'ขาย(เงินสด)'; badgeClass = 'bg-success'; break;
                    case 'transfer_in':
                      typeLabel = 'ขาย(โอน)'; badgeClass = 'bg-primary'; break;
                    case 'move_cash_to_bank':
                      typeLabel = 'ฝากเงินสด'; badgeClass = 'bg-info text-dark'; break;
                    case 'withdraw_cash_from_bank':
                      typeLabel = 'เบิกเงินสด'; badgeClass = 'bg-warning text-dark'; break;
                    case 'expense_cash_out':
                      typeLabel = 'จ่ายค่าใช้จ่าย (เงินสด)'; badgeClass = 'bg-danger text-white'; break;
                    case 'expense_transfer_out':
                      typeLabel = 'จ่ายค่าใช้จ่าย (โอน)'; badgeClass = 'bg-danger text-white'; break;
                    case 'initial_balance_cash':
                      typeLabel = 'ตั้งยอดเริ่มต้น(เงินสด)'; badgeClass = 'bg-dark text-white'; break;
                    case 'initial_balance_transfer':
                      typeLabel = 'ตั้งยอดเริ่มต้น(โอน)'; badgeClass = 'bg-dark text-white'; break;
                    case 'deposit_cash_in':
                      typeLabel = 'มัดจำ(เงินสด)'; badgeClass = 'bg-success'; break;
                    case 'deposit_transfer_in':
                      typeLabel = 'มัดจำ(โอน)'; badgeClass = 'bg-primary'; break;
                    case 'deposit_refund_cash':
                      typeLabel = 'คืนมัดจำ(เงินสด)'; badgeClass = 'bg-warning text-dark'; break;
                    case 'deposit_refund_transfer':
                      typeLabel = 'คืนมัดจำ(โอน)'; badgeClass = 'bg-warning text-dark'; break;
                    default:
                      break;
                  }
                  // สร้างข้อความคอลัมน์ "รายการ"
                  let itemDesc = '-';
                  let onItemClick = undefined;
                  let clickable = false;
                  if (m.order_id && m.order_code) {
                    itemDesc = `รายได้จากบิล ${m.order_code}`;
                    onItemClick = () => handleShowOrderDetail(m.order_id);
                    clickable = true;
                  } else if (m.movement_type?.startsWith('expense_')) {
                    // Show expense details: code + category + note
                    console.log('[CashflowPage] Expense movement:', m);
                    const parts = [];
                    if (m.expense_code) parts.push(m.expense_code);
                    if (m.expense_category) parts.push(`[${m.expense_category}]`);
                    if (m.note) parts.push(m.note);
                    if (m.responsible_name) parts.push(`(ผู้รับผิดชอบ: ${m.responsible_name})`);

                    if (m.responsible_name) parts.push(`(ผู้รับผิดชอบ: ${m.responsible_name})`);

                    itemDesc = parts.length > 0 ? parts.join(' ') : 'ค่าใช้จ่าย';
                    if (m.expense_id) {
                      onItemClick = () => handleShowExpenseDetail(m.expense_id);
                      clickable = true;
                    }
                  } else if (m.deposit_id) {
                    itemDesc = `มัดจำ #${m.deposit_id} ${m.note ? '- ' + m.note : ''}`;
                    onItemClick = () => handleShowDepositDetail(m.deposit_id);
                    clickable = true;
                  } else {
                    itemDesc = m.note || '-';
                  }

                  return (
                    <tr key={m.id}>
                      <td className="text-muted small">{timeStr}</td>
                      <td><span className={`badge ${badgeClass}`}>{typeLabel}</span></td>
                      <td>
                        {clickable ? (
                          <button type="button" className="btn btn-link p-0 text-decoration-none" onClick={onItemClick}>
                            {itemDesc}
                          </button>
                        ) : (
                          <span className="text-muted">{itemDesc}</span>
                        )}
                      </td>
                      <td className="text-end fw-bold">{fmt(m.amount)} ฿</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-light">
                  <td colSpan={2} className="text-end text-muted">รายรับ</td>
                  <td className="text-end text-success fw-bold">+{fmt(totals.totalIn)} ฿</td>
                  <td></td>
                </tr>
                <tr className="bg-light">
                  <td colSpan={2} className="text-end text-muted">รายจ่าย/คืน</td>
                  <td className="text-end text-danger fw-bold">-{fmt(totals.totalOut)} ฿</td>
                  <td></td>
                </tr>
                <tr className="fw-bold bg-secondary text-white">
                  <td colSpan={2} className="text-end">สุทธิ</td>
                  <td className="text-end">{totals.net >= 0 ? '+' : ''}{fmt(totals.net)} ฿</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <div className="d-flex justify-content-between align-items-center mt-2">
              <div className="text-muted small">แสดง {totalCount === 0 ? 0 : pageStart + 1}-{pageEnd} จาก {totalCount} รายการ</div>
              <div className="btn-group">
                <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>ก่อนหน้า</button>
                <span className="btn btn-sm btn-light disabled">หน้า {page}/{totalPages}</span>
                <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>ถัดไป</button>
              </div>
            </div>
          </div>
        </div >
      </div >

      {/* removed Bill Detail Modal */}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        orderDetail={selectedOrderDetail}
        loading={loadingOrderDetail}
        formatMoney={fmt}
      />

      {/* Expense Detail Modal */}
      <ExpenseDetailModal
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        expense={selectedExpense}
        loading={loadingExpense}
        formatMoney={fmt}
      />
      <DepositDetailModal
        isOpen={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
        deposit={selectedDeposit}
      />

      {/* Movement Modal (input amount + note, then confirm) */}
      {
        confirmModal.show && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-pencil-square me-2"></i>{confirmModal.title}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setConfirmModal({ ...confirmModal, show: false })}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">จำนวนเงิน (บาท)</label>
                    <input type="number" className="form-control" value={confirmModal.amount} onChange={e => setConfirmModal({ ...confirmModal, amount: e.target.value })} placeholder="0.00" disabled={loading} />
                    <div className="form-text">{confirmModal.summary}</div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">หมายเหตุ (ถ้ามี)</label>
                    <input type="text" className="form-control" value={confirmModal.note} onChange={e => setConfirmModal({ ...confirmModal, note: e.target.value })} placeholder="เช่น ฝาก KBank, เบิกทอน..." disabled={loading} />
                  </div>
                  <div className="small text-muted">
                    {confirmModal.action === 'move_cash_to_bank' && `ยอดเงินสดคงเหลือ: ${fmt(cashBalance)} ฿`}
                    {confirmModal.action === 'withdraw_cash_from_bank' && `ยอดในบัญชี (โอน) คงเหลือ: ${fmt(transferBalance)} ฿`}
                    {confirmModal.action === 'set_initial_cash' && `ยอดเงินสดปัจจุบัน: ${fmt(cashBalance)} ฿`}
                    {confirmModal.action === 'set_initial_transfer' && `ยอดในบัญชีปัจจุบัน: ${fmt(transferBalance)} ฿`}
                  </div>
                </div>
                <div className="modal-footer justify-content-center">
                  <button type="button" className="btn btn-secondary px-4" onClick={() => setConfirmModal({ ...confirmModal, show: false })} disabled={loading}>
                    ยกเลิก
                  </button>
                  <button type="button" className="btn btn-primary px-4" onClick={handleConfirmMove} disabled={loading}>
                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-check-circle me-2"></i>}
                    ยืนยัน
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default CashflowPage;
